import { useMemo } from 'react';
import {
  Button,
  Card,
  Column,
  Divider,
  GridContainer,
  Li,
  Link,
  Row,
  Ul,
  VisuallyHidden,
} from '@vp/swan';
import { useTranslations } from '@vp/om-translations';
import { ModTarget } from '@vp/mod-father';
import { useQuery } from '@tanstack/react-query';
import { HistoryLineItem } from './HistoryLineItem';
import { useSiteData } from '../../hooks/useSiteData';
import {
  generateLineItemListDisplay,
  isObjectLineItemGroupContainer,
} from '../../utils/lineItemListUtils';
import { GET_ORDER_FEEDBACK_GROUPS } from '../../constants/reactQueryKeys';
import { getFeedbackGroupsAwaitingFeedback } from '../../apis/orderUpdateRequestsApi';
import { customerFeedbackTypesFilter } from '../../constants/customerFeedback';
import { CustomerFeedbackGroupsProvider } from '../contexts/CustomerFeedbackGroupsContext';
import { OrderInfoDisplay } from '../shared/OrderInfoDisplay';
import { useShopperId } from '../../hooks/useShopperId';
import { HistoryOrderUxModWrapper } from '../uxMods/HistoryOrderUxModWrapper';
import { useOrderSummary } from '../contexts/OrderSummaryContext';
import { LineItemGroup } from './itemGrouping/LineItemGroup';

export const Order = () => {
  const {
    order: { lineItems, orderNumber },
  } = useOrderSummary();

  const shopperId = useShopperId();
  const {
    tenantEnvironment: environment,
    externalAppNavigation: { orderDetails: orderDetailsUrl },
  } = useSiteData();

  const [orderHistoryMessages = {}] = useTranslations(['orderHistoryMessages']);

  const { data: feedbackGroups } = useQuery({
    queryKey: [GET_ORDER_FEEDBACK_GROUPS, orderNumber, shopperId, environment],

    queryFn: async () =>
      getFeedbackGroupsAwaitingFeedback({
        orderNumber,
        shopperId,
        types: customerFeedbackTypesFilter,
        environment,
      }) || [],

    enabled: !!orderNumber && !!shopperId,
  });

  /** *************************** */
  // Determine the logical sorting of line items and line item groups relative to prmomised arrival dates
  const filterOutPastArrivalDates = true;
  const lineItemListDisplay = useMemo(
    () => generateLineItemListDisplay(lineItems, filterOutPastArrivalDates),
    [filterOutPastArrivalDates, lineItems]
  );

  return (
    <CustomerFeedbackGroupsProvider feedbackGroups={feedbackGroups || []}>
      <Divider />
      <Card p="0">
        <HistoryOrderUxModWrapper>
          <GridContainer>
            <Row>
              <Column py={{ xs: 5, sm: 6 }} span={4}>
                <OrderInfoDisplay />
                <Button
                  render={(p) => (
                    <Link
                      href={`${orderDetailsUrl}?orderId=${orderNumber}`}
                      className={p.className}
                    >
                      {p.children}
                    </Link>
                  )}
                  skin="primary"
                >
                  {orderHistoryMessages.goToOrderDetailsButtonText}
                  <VisuallyHidden>{orderNumber}</VisuallyHidden>
                </Button>

                <ModTarget name="belowHistorySummary" />
              </Column>

              <Column p={{ xs: 5, sm: 6 }} span={8}>
                <Ul skin="minimal" data-testid={`order-${orderNumber}`}>
                  {lineItemListDisplay.map((displayGroup) =>
                    isObjectLineItemGroupContainer(displayGroup) ? (
                      <Li key={`groupId-${displayGroup.groupId}`}>
                        <LineItemGroup
                          items={displayGroup.lineItems}
                          groupId={displayGroup.groupId}
                        />
                      </Li>
                    ) : (
                      // If a displayGroup is a standalone item (no groupId), it will contain a lineItems array with that single item: { lineItems: [ itemA ] }
                      <Li
                        key={`item-${displayGroup.id}`}
                        data-testid={`item-${displayGroup.id}`}
                        pb={{ xs: 7, sm: 6 }}
                      >
                        <HistoryLineItem item={displayGroup} />
                      </Li>
                    )
                  )}
                </Ul>
              </Column>
            </Row>
          </GridContainer>
        </HistoryOrderUxModWrapper>
      </Card>
    </CustomerFeedbackGroupsProvider>
  );
};
